import { useReducer, useCallback, useRef, useEffect } from 'react';
import { leadGenerationService } from '../services/leadGenerationService';
import { validateLeadGenerationForm, sanitizeInput } from '../utils/validation';

interface FormState {
  companyName: string;
  geography: string;
  isSubmitting: boolean;
  isSubmitted: boolean;
  error: string | null;
  validationErrors: {
    companyName?: string;
    geography?: string;
  };
}

type FormAction =
  | { type: 'SET_COMPANY_NAME'; payload: string }
  | { type: 'SET_GEOGRAPHY'; payload: string }
  | { type: 'SET_VALIDATION_ERROR'; field: 'companyName' | 'geography'; error: string | undefined }
  | { type: 'SUBMIT_START' }
  | { type: 'CLEAR_FIELDS' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR'; payload: string }
  | { type: 'RESET_FORM' };

const initialState: FormState = {
  companyName: '',
  geography: '',
  isSubmitting: false,
  isSubmitted: false,
  error: null,
  validationErrors: {},
};

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_COMPANY_NAME':
      return {
        ...state,
        companyName: action.payload,
        error: null,
        validationErrors: { ...state.validationErrors, companyName: undefined }
      };
    case 'SET_GEOGRAPHY':
      return {
        ...state,
        geography: action.payload,
        error: null,
        validationErrors: { ...state.validationErrors, geography: undefined }
      };
    case 'SET_VALIDATION_ERROR':
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.field]: action.error
        }
      };
    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: null };
    case 'CLEAR_FIELDS':
      return {
        ...state,
        companyName: '',
        geography: '',
        validationErrors: {},
        error: null
      };
    case 'SUBMIT_SUCCESS':
      return { ...state, isSubmitting: false, isSubmitted: false, error: null };
    case 'SUBMIT_ERROR':
      return { ...state, isSubmitting: false, error: action.payload };
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
}

export const useLeadGenerationForm = (userId?: string) => {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const submissionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSubmissionRef = useRef<string | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const submissionTimestampRef = useRef<number | null>(null);

  const setCompanyName = useCallback((value: string) => {
    dispatch({ type: 'SET_COMPANY_NAME', payload: value });
  }, []);

  const setGeography = useCallback((value: string) => {
    dispatch({ type: 'SET_GEOGRAPHY', payload: value });
  }, []);

  const sanitizedCompanyName = sanitizeInput(state.companyName);
  const sanitizedGeography = sanitizeInput(state.geography);

  const validateAndSubmit = useCallback(async () => {
    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Check if user is authenticated
    if (!userId) {
      dispatch({ type: 'SUBMIT_ERROR', payload: 'You must be logged in to generate leads' });
      return;
    }

    // Prevent duplicate submissions
    const now = Date.now();
    const lastSubmissionKey = `${sanitizedCompanyName}-${sanitizedGeography}`;

    if (lastSubmissionRef.current === lastSubmissionKey &&
        (now - (submissionTimestampRef.current || 0)) < 5000) {
      console.log('Duplicate submission prevented');
      return;
    }

    const validation = validateLeadGenerationForm(sanitizedCompanyName, sanitizedGeography);

    if (!validation.isValid) {
      dispatch({ type: 'SUBMIT_ERROR', payload: validation.error! });
      return;
    }

    // Mark as submitting and prevent duplicates
    dispatch({ type: 'SUBMIT_START' });
    lastSubmissionRef.current = lastSubmissionKey;
    submissionTimestampRef.current = now;

    try {
      // Clear fields immediately and show "Sent" state
      dispatch({ type: 'CLEAR_FIELDS' });

      console.log(`🚀 Starting lead generation for: ${sanitizedCompanyName} in ${sanitizedGeography}`);
      console.log(`📝 User ID: ${userId}, Submission Key: ${lastSubmissionKey}`);
      console.log(`⏱️  Note: AI processing may take 1-5 minutes - status will update in real-time`);

      // Call the service with user ID - this will create the status record first
      leadGenerationService.generateLead(
        userId,
        sanitizedCompanyName,
        sanitizedGeography
      ).catch(error => {
        console.error('❌ Lead generation service call failed:', error);
        // Don't show error immediately - the backend might still be processing
        // The status will be updated by the backend based on actual processing result
        // Only show error for immediate validation failures
        if (error.message.includes('Invalid input') || error.message.includes('must be logged in')) {
          dispatch({
            type: 'SUBMIT_ERROR',
            payload: error.message
          });
        }
      });

      // Reset submitting state after brief delay
      submissionTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'SUBMIT_SUCCESS' });
        // Clear the duplicate prevention after successful completion
        setTimeout(() => {
          lastSubmissionRef.current = null;
          submissionTimestampRef.current = null;
          console.log('✅ Submission tracking cleared, allowing new submissions');
        }, 10000); // Allow resubmission after 10 seconds
      }, 1500);

    } catch (error: any) {
      console.error('❌ Lead generation form error:', error);
      dispatch({ type: 'SUBMIT_ERROR', payload: error.message || 'An unexpected error occurred' });
    }
  }, [sanitizedCompanyName, sanitizedGeography, userId]);

  const resetForm = useCallback(() => {
    // Clear any pending timeouts
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
      submissionTimeoutRef.current = null;
    }
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    lastSubmissionRef.current = null;
    submissionTimestampRef.current = null;
    dispatch({ type: 'RESET_FORM' });
  }, []);

  const isFormValid = useCallback(() => {
    const sanitizedCompanyName = sanitizeInput(state.companyName);
    const sanitizedGeography = sanitizeInput(state.geography);
    const validation = validateLeadGenerationForm(sanitizedCompanyName, sanitizedGeography);
    return validation.isValid;
  }, [state.companyName, state.geography]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (submissionTimeoutRef.current) {
        clearTimeout(submissionTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    state,
    actions: {
      setCompanyName,
      setGeography,
      validateAndSubmit,
      resetForm,
    },
    computed: {
      isFormValid: isFormValid(),
      canSubmit: isFormValid() && !state.isSubmitting,
    }
  };
};